import { useContext, useEffect, useReducer} from "react";
// Import App Context 
import { AppContext } from "../../../../../../core/contexts/AppContext";
// Import components
import InputTextComponent from "../../../../../../shared/components/InputTextComponent/InputTextComponent";
// Constants Import
import {
    generateCaptchaURL,
    validateCaptchaURL,
} from "../../constants/constants";
// Reducer Import
import { IVehicleDataResponse } from "../../reducers/revisioniReducer/revisioniReducer";
// SCSS file import
import "./CaptchaValidationSection.scss";
// Utils import
import { showDialogFailed } from "../../../../../../shared/utils/DialogUtils";

// import Button component
import ButtonWithIconAlt from "../../../../../../shared/components/buttons/ButtonWithIconAlt/ButtonWithIconAlt";

// Interfaccia che definisce la struttura della risposta dell'img captcha
interface ICaptchaImgResponse {
    id: number;
    image: string;
}

// Interfaccia che definisce la struttura della risposta del captcha-validator
interface ICaptchaValidatorResponse {
    guid: string;
}

// Definizione del tipo di stato iniziale
type State = {
    captchaFieldValue: string;
    captchaImg: ICaptchaImgResponse | undefined;
    captchaImgSpinner: boolean;
    captchaImgError: Error | undefined;
    captchaValidator: ICaptchaValidatorResponse | undefined;
    captchaValidatorSpinner: boolean;
    captchaValidatorError: Error | undefined;
    captchaValidatorClass: boolean;
};

// Definizione del tipo di azioni
type Action =
    | { type: "SET_CAPTCHA_FIELD_VALUE"; payload: string }
    | { type: "SET_CAPTCHA_IMG"; payload: ICaptchaImgResponse }
    | { type: "SET_CAPTCHA_IMG_SPINNER"; payload: boolean }
    | { type: "SET_CAPTCHA_IMG_ERROR"; payload: Error | undefined }
    | { type: "SET_CAPTCHA_VALIDATOR_CLASS"; payload: boolean }
    | {
          type: "SET_CAPTCHA_VALIDATOR";
          payload: ICaptchaValidatorResponse | undefined;
      }
    | { type: "SET_CAPTCHA_VALIDATOR_SPINNER"; payload: boolean }
    | { type: "SET_CAPTCHA_VALIDATOR_ERROR"; payload: Error | undefined };

// Stato iniziale
const initialState: State = {
    captchaFieldValue: "",
    captchaImg: undefined,
    captchaImgSpinner: true,
    captchaImgError: undefined,
    captchaValidator: undefined,
    captchaValidatorSpinner: false,
    captchaValidatorError: undefined,
    captchaValidatorClass: false,
};

// Reducer per gestire lo stato
// ad ogni azione che viene invocata con il dispatch 
// il reduced restituisce lo stato corrente ed il payload dell'azione
function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "SET_CAPTCHA_FIELD_VALUE":
            return {
                ...state,
                captchaFieldValue: action.payload,
            };
        case "SET_CAPTCHA_IMG":
            return {
                ...state,
                captchaImg: action.payload,
            };
        case "SET_CAPTCHA_IMG_SPINNER":
            return {
                ...state,
                captchaImgSpinner: action.payload,
            };
        case "SET_CAPTCHA_IMG_ERROR":
            return {
                ...state,
                captchaImgError: action.payload,
            };
            case "SET_CAPTCHA_VALIDATOR_CLASS":
                return {
                    ...state,
                    captchaValidatorClass: action.payload,
                };            
        case "SET_CAPTCHA_VALIDATOR":
            return {
                ...state,
                captchaValidator: action.payload,
            };
        case "SET_CAPTCHA_VALIDATOR_SPINNER":
            return {
                ...state,
                captchaValidatorSpinner: action.payload,
            };
        case "SET_CAPTCHA_VALIDATOR_ERROR":
            return {
                ...state,
                captchaValidatorError: action.payload,
            };
        default:
            return state;
    }
}

// struttura dati per le props del componente
// onAuthCaptcha : memorizza i dati del Captcha, stringa vuota se non validato
// onClearExecuted : memorizza se eseguito il Clear
// isClearButtonClicked : memorizza se premuto il pulsante ClearButton
// vehicleData : memorizza i dati di risposta dell'interrogazione sul veicolo
type CaptchaValidationOutlinedPaperProps = {
    onAuthCaptcha: (str: string) => void;
    onClearExecuted: (val: boolean) => void;
    isClearButtonClicked: boolean;
    vehicleData: IVehicleDataResponse | undefined;
};

/* CaptchaValidationSection ha uno stato interno che tiene principalmente 
    traccia del valore del captcha inserito dall'utente (captchaFieldValue) 
    e dell'immagine captcha da mostrare (captchaImg). Il componente in questione mostra
    un'immagine ed un campo di input utilizzato per controllare i dati inseriti dell'utente. 
    Al submit del valore del field viene richiamata la funzione validateCaptcha per 
    verificare che la risposta sia corretta */
const CaptchaValidationSection = (
    props: CaptchaValidationOutlinedPaperProps
) => {
    const { appText } = useContext(AppContext);
    const { REVISIONI_TEXT } = appText;
    const [state, dispatch] = useReducer(reducer, initialState);
    /* Recupera l'URL dell'immagine captcha da mostrare */
    function fetchImageURL() {
        dispatch({ type: "SET_CAPTCHA_IMG_SPINNER", payload: true });
        const url = generateCaptchaURL;
        
        
        const requestOptions = {
            method: "POST",
        };
        
        fetch(url, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Errore response fetch-captcha");
                }
                return response.json();
            })
            .then((data) => {
                dispatch({ type: "SET_CAPTCHA_IMG", payload: data });
                dispatch({ type: "SET_CAPTCHA_IMG_SPINNER", payload: false });
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_CLASS",
                    payload: false,
                });
            })
            .catch((error) => {
                dispatch({ type: "SET_CAPTCHA_IMG_ERROR", payload: error });
                dispatch({ type: "SET_CAPTCHA_IMG_SPINNER", payload: false });
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_CLASS",
                    payload: false,
                });
            });
    }

    /* Determina se il captcha inserito è valido */
    function validateCaptcha(id: number, text: string) {

        dispatch({ type: "SET_CAPTCHA_VALIDATOR_SPINNER", payload: true });
        const url = validateCaptchaURL;
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ id, text }),
        };
        fetch(url, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Errore response validate-captcha");
                }
                return response.json();
            })
            .then((data: ICaptchaValidatorResponse) => {
                dispatch({ type: "SET_CAPTCHA_VALIDATOR", payload: data });
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_SPINNER",
                    payload: false,
                });
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_CLASS",
                    payload: true,
                });
                props.onAuthCaptcha(data.guid);
            })
            .catch((error) => {
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_ERROR",
                    payload: error,
                });
                dispatch({
                    type: "SET_CAPTCHA_VALIDATOR_SPINNER",
                    payload: false,
                });
                dispatch({
                    type: "SET_CAPTCHA_FIELD_VALUE",
                    payload: "",
                });
                showDialogFailed(REVISIONI_TEXT.CAPTCHA_VALIDATION_ERROR_CODE, REVISIONI_TEXT.CAPTCHA_VALIDATION_ERROR_MSG);
                fetchImageURL();
            });
    }

    /* Viene chiamata la funzione fetchImageURL all'avvio del componente,
    in modo da recuperare subito l'immagine captcha da mostrare. */
    useEffect(() => {
        fetchImageURL();
    }, []);

    /* Rimane in ascolto del cambiamento del valore della prop 
    isClearButtonClicked per pulire lo stato */
    useEffect(() => {
        if (props.isClearButtonClicked) {
            dispatch({ type: "SET_CAPTCHA_FIELD_VALUE", payload: "" });
            dispatch({ type: "SET_CAPTCHA_VALIDATOR", payload: undefined });
            dispatch({
                type: "SET_CAPTCHA_VALIDATOR_CLASS",
                payload: false,
            });
            fetchImageURL();
            props.onClearExecuted(false);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isClearButtonClicked]);

    function display_captchaImgSpinner(){
        if (state.captchaImgSpinner){
           return  (<div className="CaptchaValidationSection__img_spinner" />);
        }
        if (state.captchaImgError) {
            return (<h6 className="CaptchaValidationSection__error_message">
            {
                REVISIONI_TEXT.UNABLE_RETRIEVE_IMAGE_PLACEHOLDER
            }
            </h6>);
        } else {
            return ( 
                <img
                    className="CaptchaValidationSection__img"
                    alt="captcha"
                    loading="lazy"
                    src={`data:image/png;base64,${state.captchaImg?.image}`}
                />);
        }
    }

    return (
        <div className=" row">
            <div className="CaptchaValidationSection__container col-12 mt-3">
                <div className="row">
                    <div className="col-12 mb-2">
                        <h5 className="CaptchaValidationSection__title">
                            {REVISIONI_TEXT?.ENTER_CAPTCHA_TITLE ?? ""}
                        </h5>
                    </div>
                </div>
                <div className="row">
                    <div className="CaptchaValidationSection__img_container col-12 col-lg-6">
                        {/* Quando l'immagine captcha è in fase di caricamento, viene visualizzato 
                            un circular progress indicator al posto dell'immagine.
                            Se c'è un errore durante il caricamento dell'immagine, viene visualizzato
                            un messaggio di errore.
                        */}
                        {display_captchaImgSpinner()}
                    </div>
                    <div className="col-12 col-lg-6 mt-3 mt-lg-0  CaptchaValidationSection__form">
                        <div className="CaptchaValidationSection__textfield">
                            {/* Visualizza input text component del captcha
                                con hook della onChange per controllare quanto digitato
                                tramite invocazione del reducer sul valore della input text
                                passando lo stato della validazione captcha.
                                il campo e' readonly se la validazione e' stato effettuata
                                rimane readonly fino a che non si richiede cambio immagine o si fa ricerca targa
                            */}
                            <InputTextComponent
                                name="captcha"
                                //label={"Captcha"}
                                value={state.captchaFieldValue}
                                placeholder={
                                    `-- ${REVISIONI_TEXT?.ENTER_CAPTCHA_TEXTFIELD_PLACEHOLDER ?? ""} --`
                                }
                                onChange={(name: string, value: string) =>
                                    dispatch({
                                        type: "SET_CAPTCHA_FIELD_VALUE",
                                        payload: value.trim(),
                                    })
                                }
                                isRequired={false}
                                isValitated={state.captchaValidatorClass}
                                readOnly = {state.captchaValidatorClass}
                            />
                        </div>
                        <div className="row">
                            <div className="col-12 CaptchaValidationSection__buttons ">
                            {/* Visualizza pulsante valida 
                                con hook della onClick per validare quanto dentro la input text
                                il pulsante visualizza a destra uno spinner se viene lanciata
                                la validazione.
                                Al termine della validazione
                                lo spinner cessa di essere visualizzato
                            */}
                                <ButtonWithIconAlt
                                    type={"success"}
                                    size={"small"}
                                    icon={"bi bi-check2"}
                                    title={"Valida"}    
                                    cbClick={() => {
                                        if (state.captchaImg){
                                            // controlla se il testo inserito e' non vuoto
                                            if (state.captchaFieldValue === ""){
                                                dispatch({
                                                    type: "SET_CAPTCHA_VALIDATOR_ERROR",
                                                    payload: new Error("Inserire il captcha",)
                                                });                                        
                                                showDialogFailed(
                                                    REVISIONI_TEXT?.CAPTCHA_VALIDATION_ERROR_CODE ?? "", 
                                                    REVISIONI_TEXT?.CAPTCHA_VALIDATION_ERROR_MSG ?? ""
                                                );
                                                return;
                                            }
                                            validateCaptcha(
                                                state.captchaImg?.id,
                                                state.captchaFieldValue
                                            );}
                                    }}
                                />
                                {/* Visualizza pulsante cambia immagine 
                                    con hook della onClick per ricaricare l'immagine del captcha
                                */}
                                <ButtonWithIconAlt
                                    type={"outline-primary"}
                                    size={"small"}
                                    icon={"bi bi-arrow-repeat"}
                                    title={"Cambia immagine"} 
                                    cbClick={()=>{
                                        fetchImageURL();
                                        dispatch({ type: "SET_CAPTCHA_FIELD_VALUE", payload: "" });
                                        dispatch({ type: "SET_CAPTCHA_VALIDATOR", payload: undefined });
                                        }
                                    }   
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CaptchaValidationSection;
